import type { ListWelloPolicyCodeAllDto } from '__generated__/api/unauth';
import { Button, Chip } from '@common/components';
import { CustomError, isEnumValue } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { uniq } from 'es-toolkit';
import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { scroller } from 'react-scroll';
import { EXCEPTED_CODE, EXCEPTED_HIDDEN_CODE, UnauthQueryOptions, WELLO_POLICY_CODE_TYPE } from '@/api';
import { BottomNavPortal } from '@/app/_components/BottomNavPortal';
import { SEARCH_PARAMS } from '@/constants';
import { useAuthStore } from '@/stores/AuthStore';
import { Image } from '../Image/Image';
import { LoadingPageTemplate } from '../Templates/LoadingPageTemplate';
interface InterestPolicyFilterProps {
  onSubmit: (data: ListWelloPolicyCodeAllDto[]) => void;
  isSubmitting?: boolean;
  submitButtonLabel?: string;
  children?: React.ReactNode;
  initSelectedMetaData: ListWelloPolicyCodeAllDto[];
  allowOnlyC14Code?: boolean;
}
export const InterestPolicyFilter = ({
  onSubmit,
  isSubmitting,
  submitButtonLabel,
  children,
  initSelectedMetaData,
  allowOnlyC14Code
}: InterestPolicyFilterProps) => {
  const id = useId();
  const isSensitiveInfo = useAuthStore(state => state.myInfo?.sensitiveInfoYN);
  const [selectedMetaDataMap, setSelectedMetaDataMap] = useState(new Map(initSelectedMetaData.map(metaData => {
    const {
      code
    } = metaData;
    if (!code) throw new Error('metaData.code가 없습니다.');
    return [code, metaData];
  })));
  const selectedCodeList = [...selectedMetaDataMap.keys()];

  //! ⚠️ 상태 값으로 관리할 필요가 없는 값
  const subTitleCodeList: string[] = [];
  const prevSubTitleCodeList = useRef(subTitleCodeList);
  const [isClicked, setIsClicked] = useState(false);
  useEffect(() => {
    const newSubTitleCodeList = subTitleCodeList.filter(code_0 => !prevSubTitleCodeList.current.includes(code_0));
    const scrollTo = newSubTitleCodeList[newSubTitleCodeList.length - 1];

    //! 애니메이션 후 스크롤
    if (scrollTo) {
      const scrollTimmer = setTimeout(() => {
        if (!isClicked) return;
        scroller.scrollTo(scrollTo, {
          smooth: true,
          offset: -100
        });
      }, 300);
      return () => {
        clearTimeout(scrollTimmer);
        prevSubTitleCodeList.current = subTitleCodeList;
      };
    }
    return () => {
      prevSubTitleCodeList.current = subTitleCodeList;
    };
  });
  const {
    data
  } = useSuspenseQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/wello-policy-code/all'](),
    staleTime: Infinity,
    gcTime: Infinity,
    select: ({
      context
    }) => {
      const {
        contents
      } = context ?? {};
      if (!contents) throw new CustomError({
        return_message: '정보가 없습니다.'
      });
      const boundedMetaData: ListWelloPolicyCodeAllDto[][] = [];
      const genderMetaData: ListWelloPolicyCodeAllDto[] = [];
      const regionMetaData: {
        mainRegion: ListWelloPolicyCodeAllDto[];
        subRegion: ListWelloPolicyCodeAllDto[];
      } = {
        mainRegion: [],
        subRegion: []
      };
      contents.forEach(content => {
        const {
          code: code_1
        } = content;
        let {
          level
        } = content;
        if (!code_1 || !level) return;
        const isHidden = code_1.includes('99') && !isEnumValue(EXCEPTED_HIDDEN_CODE, code_1);
        if (isHidden) return;
        if (code_1?.includes(EXCEPTED_CODE.GENDER)) {
          if (level === 2) genderMetaData.push(content);
          return;
        }
        if (code_1.startsWith('C14')) level = level - 1;
        if (code_1?.includes(EXCEPTED_CODE.REGION)) {
          switch (level) {
            case 2:
              return regionMetaData.mainRegion.push(content);
            case 3:
              return regionMetaData.subRegion.push(content);
            default:
              return;
          }
        }
        if (level === 3 && code_1.startsWith(EXCEPTED_CODE.UNEMPLOYMENT)) return;
        if (level === 1) return boundedMetaData.push([content]);
        const rootMetaData_0 = boundedMetaData.find(([rootMetaData]) => {
          const rootMetaDataCode = rootMetaData.code;
          return rootMetaDataCode ? code_1.includes(rootMetaDataCode) : false;
        });
        return rootMetaData_0?.push(content);
      });
      const boundedCustomPolicyMetaData: ListWelloPolicyCodeAllDto[][] = [];
      const boundedInterestMetaData = boundedMetaData.filter(metaDataList => {
        const [rootMetaData_1] = metaDataList;
        if (rootMetaData_1.welloPolicyCodeTypeCd === WELLO_POLICY_CODE_TYPE.INTEREST) {
          return true;
        } else {
          //! 관심 필터는 원래 없어서 여기서 예외처리
          if (!rootMetaData_1.welloPolicyCodeFilterCd) return false;
          boundedCustomPolicyMetaData.push(metaDataList);
          return false;
        }
      });
      return {
        customPolicyMetaDataList: boundedCustomPolicyMetaData.flat(),
        interestedCategoryMetaDataList: boundedInterestMetaData.flat(),
        genderMetaData,
        regionMetaData
      };
    }
  });
  const interestedCategoryMetaDataList = allowOnlyC14Code ? data.interestedCategoryMetaDataList.filter(metaData_0 => metaData_0.code && metaData_0.code.startsWith('C14')) : data.interestedCategoryMetaDataList;
  const tempParentMetaDataMap = new Map<string, ListWelloPolicyCodeAllDto>();
  const applySelectedMap = () => setSelectedMetaDataMap(selectedMetaDataMap_0 => new Map(selectedMetaDataMap_0));
  return !interestedCategoryMetaDataList ? <LoadingPageTemplate /> : <form className="pb-96 mobile-tablet:pb-0" id={id} onSubmit={e => {
    e.preventDefault();
    onSubmit([...selectedMetaDataMap.values()]);
  }}>
      <dl className="m-0 flex flex-wrap items-start justify-start gap-6 px-20">
        {interestedCategoryMetaDataList?.map(metaData_1 => {
        const {
          code: code_2,
          value,
          parentCode: parent_code
        } = metaData_1;
        let {
          level: level_0
        } = metaData_1;
        if (!code_2 || !level_0) return null;
        if (code_2.startsWith('C14')) level_0 = level_0 - 1;
        switch (level_0) {
          case 1:
            {
              tempParentMetaDataMap.set(code_2, metaData_1);
              return <dt key={code_2} className="mb-4 mt-26 flex w-full items-center gap-4 text-Gray-800 typo-body4" id={code_2}>
                  {metaData_1.imageUrl ? <Image alt="" height={18} src={metaData_1.imageUrl} width={18} /> : null}
                  {value}
                </dt>;
            }
          case 2:
            {
              if (!parent_code) return null;
              const rootMetaData_2 = tempParentMetaDataMap.get(parent_code);
              if (!rootMetaData_2) return null;
              const isDuplicatable_0 = rootMetaData_2.duplicateAvailYN;
              return <dd key={code_2} className="m-0 w-fit">
                  <Chip checked={selectedMetaDataMap.has(code_2)} className="px-10 py-5 typo-body4" onCheckedChange={checked_0 => {
                  if (metaData_1.sensitiveInfoYN && !isSensitiveInfo) return;

                  //* 최신 선택된 코드 리스트를 가져오기 위한 함수
                  const getSelectedCodeList = () => [...selectedMetaDataMap.keys()];
                  if (checked_0) {
                    if (isDuplicatable_0) {
                      selectedMetaDataMap.set(code_2, metaData_1);
                    } else {
                      const duplicatedCode_0 = getSelectedCodeList().filter(code_5 => code_5.includes(parent_code));
                      duplicatedCode_0.forEach(code_6 => {
                        selectedMetaDataMap.delete(code_6);
                      });
                      selectedMetaDataMap.set(code_2, metaData_1);
                    }
                  } else {
                    getSelectedCodeList().forEach(selectedCode_0 => {
                      if (selectedCode_0.includes(code_2)) selectedMetaDataMap.delete(selectedCode_0);
                    });
                  }

                  //* 해당사항 없음에 대한 예외처리
                  if (code_2.includes(EXCEPTED_CODE.TARGET_CHARACTERISTICS)) {
                    const isTargetEmpty = code_2 === EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY;
                    if (checked_0) {
                      //* 해당 사항 없음 선택시 다른 선택된 대상 특성 삭제
                      if (isTargetEmpty) {
                        getSelectedCodeList().forEach(code_7 => {
                          if (code_7.includes(EXCEPTED_CODE.TARGET_CHARACTERISTICS) && code_7 !== EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY) selectedMetaDataMap.delete(code_7);
                        });
                      } //* 다른 대상 특성 선택시 해당 사항 없음 삭제
                      else {
                        selectedMetaDataMap.delete(EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY);
                      }
                    } //* 대상 특성에 아무것도 선택하지 않았을 때 해당 사항 없음 추가
                    else {
                      const hasSelectedTargetCharacteristics = getSelectedCodeList().some(code_8 => code_8.includes(EXCEPTED_CODE.TARGET_CHARACTERISTICS));
                      if (!hasSelectedTargetCharacteristics) {
                        const targetCharacteristicsEmptyMetaData = interestedCategoryMetaDataList.find(({
                          code: code_9
                        }) => code_9 === EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY);
                        if (!targetCharacteristicsEmptyMetaData) throw new Error('meta data에 대상 특성 해당 없음이 없습니다.');
                        selectedMetaDataMap.set(EXCEPTED_HIDDEN_CODE.TARGET_CHARACTERISTICS_EMPTY, targetCharacteristicsEmptyMetaData);
                      }
                    }
                  }
                  applySelectedMap();
                }} onClick={() => {
                  if (metaData_1.sensitiveInfoYN) {
                    if (isSensitiveInfo) {
                      setIsClicked(true);
                    } else {
                      const url_0 = new URL(window.location.href);
                      url_0.searchParams.set(SEARCH_PARAMS.POLICY_FILTER, 'true');
                      history.replaceState(null, '', url_0.search);
                    }
                  }
                }}>
                    {value}
                  </Chip>
                </dd>;
            }
          default:
            {
              if (!parent_code) return null;
              const hasSelectedParentMetaData = selectedMetaDataMap.has(parent_code);
              if (!hasSelectedParentMetaData) return null;

              //! ⚠️ 서버에서 받아온 선택했던 값들은 메타 데이터 값의 정보들이 완벽하지 않음 (ex. duplicate_avail_yn가 모두 false로 나오는 등)
              const parentMetaData = selectedMetaDataMap.get(parent_code);
              if (!parentMetaData) return null;
              const isFirstChild = !subTitleCodeList.includes(parent_code);
              const isDuplicatable = parentMetaData.duplicateAvailYN;
              if (isFirstChild) {
                // eslint-disable-next-line react-hooks/immutability -- 렌더 중 로컬 배열 누적 추적 패턴, 부모코드별 첫 자식 여부 판별용
                subTitleCodeList.push(parent_code);
              }
              return <Fragment key={code_2}>
                  {isFirstChild ? <dt className="mb-4 mt-26 w-full animate-fade-up text-Gray-800 duration-300 typo-body4" id={parent_code}>
                      {metaData_1.imageUrl ? <Image height={18} src={metaData_1.imageUrl} width={18} alt="필터 아이콘" className="object-contain" /> : null}
                      {parentMetaData.value}
                    </dt> : null}
                  <dd className="m-0 w-fit animate-fade-up duration-300">
                    <Chip checked={selectedMetaDataMap.has(code_2)} className="px-10 py-5 typo-body4" onChange={e_0 => {
                    if (metaData_1.sensitiveInfoYN && !isSensitiveInfo) return;
                    const {
                      checked
                    } = e_0.target;
                    if (checked) {
                      if (isDuplicatable) {
                        selectedMetaDataMap.set(code_2, metaData_1);
                      } else {
                        const duplicatedCode = selectedCodeList.filter(code_3 => code_3 !== parent_code && code_3.includes(parent_code));
                        duplicatedCode.forEach(code_4 => {
                          selectedMetaDataMap.delete(code_4);
                        });
                        selectedMetaDataMap.set(code_2, metaData_1);
                      }
                    } else {
                      selectedCodeList.forEach(selectedCode => {
                        if (selectedCode.includes(code_2)) selectedMetaDataMap.delete(selectedCode);
                      });
                    }
                    applySelectedMap();
                  }} onClick={() => {
                    if (metaData_1.sensitiveInfoYN) {
                      if (isSensitiveInfo) {
                        setIsClicked(true);
                      } else {
                        const url = new URL(window.location.href);
                        url.searchParams.set(SEARCH_PARAMS.POLICY_FILTER, 'true');
                        history.replaceState(null, '', url.search);
                      }
                    }
                  }}>
                      {value}
                    </Chip>
                  </dd>
                </Fragment>;
            }
        }
      })}
      </dl>
      <BottomNavPortal hasShadow={false}>
        <footer className="flex w-full gap-8 px-20 py-16">
          {children}
          <Button className="flex-1" disabled={(() => {
          if (isSubmitting) return true;
          const selectedParentCodeOfCodeList: string[] = [];
          const hasInterestCode = selectedCodeList.some(code_10 => code_10.startsWith('C14'));
          if (!hasInterestCode) return true;
          selectedMetaDataMap.forEach(metaData_2 => {
            const {
              parentCode: parent_code_0
            } = metaData_2;
            if (parent_code_0) selectedParentCodeOfCodeList.push(parent_code_0);
          });

          //* 선택안한 3차 이상 메타데이터 확인
          const tempHasChildrenSubMetaCodeList: string[] = [];
          interestedCategoryMetaDataList.forEach(({
            level: level_1,
            parentCode: parent_code_1
          }) => {
            if (2 < (level_1 ?? 0) && parent_code_1) tempHasChildrenSubMetaCodeList.push(parent_code_1);
          });
          const hasChildrenSubMetaCodeList = uniq(tempHasChildrenSubMetaCodeList);
          for (const [code_11, metaCode] of selectedMetaDataMap.entries()) {
            if ((metaCode.level ?? 0) < 2) continue;
            const hasChildren = hasChildrenSubMetaCodeList.includes(code_11);
            if (!hasChildren) continue;
            const isChildrenSelected = selectedParentCodeOfCodeList.includes(code_11);
            if (!isChildrenSelected) return true;
          }
          return false;
        })()} form={id} loading={isSubmitting} type="submit">
            {submitButtonLabel}
          </Button>
        </footer>
      </BottomNavPortal>
    </form>;
};